const getBaseOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost';
};

export const normalizeCapabilityPath = (value = '/') => {
  if (!value || typeof value !== 'string') {
    return '/';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '/';
  }

  try {
    const url = new URL(trimmed, getBaseOrigin());
    const normalized = url.pathname.replace(/\/+$/, '');
    return normalized || '/';
  } catch {
    const normalized = trimmed.split('?')[0].split('#')[0].replace(/\/+$/, '');
    return normalized || '/';
  }
};

export const parseCapabilityHref = (href = '/') => {
  const url = new URL(href || '/', getBaseOrigin());
  return {
    path: normalizeCapabilityPath(url.pathname),
    params: Object.fromEntries(url.searchParams.entries())
  };
};

const matchesBindingParams = (bindingParams = {}, candidateParams = {}) =>
  Object.entries(bindingParams).every(([key, value]) => candidateParams?.[key] === String(value));

export const matchesHrefBinding = (binding, href) => {
  if (!binding || !href) {
    return false;
  }

  const candidate = parseCapabilityHref(href);

  if (binding.kind === 'path') {
    return normalizeCapabilityPath(binding.target) === candidate.path;
  }

  if (binding.kind === 'query') {
    return normalizeCapabilityPath(binding.target) === candidate.path
      && matchesBindingParams(binding.params, candidate.params);
  }

  if (binding.kind === 'route_id' && binding.path) {
    return normalizeCapabilityPath(binding.path) === candidate.path;
  }

  return false;
};

export const matchesScreenBinding = (binding, screen) =>
  Boolean(binding && binding.kind === 'screen' && binding.target === screen);

export const matchesRouteIdBinding = (binding, routeId) =>
  Boolean(binding && binding.kind === 'route_id' && binding.target === routeId);

export const findCapabilityEntry = (entries = [], matcher) => {
  if (!Array.isArray(entries) || typeof matcher !== 'function') {
    return null;
  }

  return entries.find((entry) => Array.isArray(entry?.bindings) && entry.bindings.some(matcher)) || null;
};

export const buildCapabilityStatus = (entry) => ({
  managed: Boolean(entry),
  state: entry?.state || 'unmanaged',
  reason: entry?.reason || null,
  entry: entry || null
});

export const isCapabilityAccessible = (statusOrEntry) => {
  const state = statusOrEntry?.state || statusOrEntry;
  return state !== 'absent';
};
